import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, X, ZoomIn } from 'lucide-react';

// Activity photo gallery: draggable slider + thumbnail carousel + hover zoom on
// the main image + a fullscreen lightbox with click-to-zoom & pan.
export default function ActivityGallery({ images = [], title = '' }) {
  const pics = images.length ? images : [];
  const [index, setIndex] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const [zoomed, setZoomed] = useState(false);
  const [origin, setOrigin] = useState({ x: 50, y: 50 });
  const thumbsRef = useRef(null);

  const count = pics.length;
  const go = useCallback((next) => {
    setIndex((i) => (next + count) % count);
    setZoomed(false);
  }, [count]);

  // Keep the active thumbnail in view
  useEffect(() => {
    const strip = thumbsRef.current;
    if (!strip) return;
    const active = strip.querySelector(`[data-idx="${index}"]`);
    if (active && active.scrollIntoView) {
      active.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
    }
  }, [index]);

  // Keyboard controls (arrows + Esc)
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowRight') go(index + 1);
      else if (e.key === 'ArrowLeft') go(index - 1);
      else if (e.key === 'Escape') { setLightbox(false); setZoomed(false); }
    };
    if (lightbox) {
      window.addEventListener('keydown', onKey);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [lightbox, index, go]);

  if (!count) return null;

  const onPan = (e) => {
    if (!zoomed) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setOrigin({ x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(100, y)) });
  };

  const Arrow = ({ dir, onClick, small }) => (
    <button
      type="button"
      aria-label={dir < 0 ? 'Anterior' : 'Siguiente'}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      className={`absolute top-1/2 -translate-y-1/2 ${dir < 0 ? 'left-3' : 'right-3'} z-10 flex items-center justify-center rounded-full bg-white/90 text-[#14213d] shadow-md transition hover:bg-white dark:bg-slate-900/80 dark:text-white dark:hover:bg-slate-900 ${small ? 'h-9 w-9' : 'h-11 w-11'}`}
    >
      {dir < 0 ? <ChevronLeft className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
    </button>
  );

  return (
    <div className="mb-12">
      {/* Main viewer */}
      <div
        className="group relative aspect-[16/10] w-full cursor-zoom-in overflow-hidden rounded-2xl bg-gray-100"
        onClick={() => setLightbox(true)}
      >
        <img
          src={pics[index]}
          alt={`${title} — ${index + 1}`}
          className="h-full w-full object-cover transition-transform duration-[600ms] ease-out group-hover:scale-110"
        />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/10 to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
        <span className="pointer-events-none absolute right-3 top-3 inline-flex items-center gap-1 rounded-full bg-black/55 px-3 py-1 text-xs font-semibold text-white backdrop-blur">
          <ZoomIn className="h-3.5 w-3.5" /> {index + 1}/{count}
        </span>
        {count > 1 && (
          <>
            <Arrow dir={-1} onClick={() => go(index - 1)} />
            <Arrow dir={1} onClick={() => go(index + 1)} />
          </>
        )}
      </div>

      {/* Thumbnail carousel */}
      {count > 1 && (
        <div
          ref={thumbsRef}
          className="mt-3 flex gap-3 overflow-x-auto pb-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
        >
          {pics.map((img, i) => (
            <button
              key={`${img}-${i}`}
              data-idx={i}
              type="button"
              aria-label={`${title} — ${i + 1}`}
              onClick={() => go(i)}
              className={`relative aspect-[4/3] h-16 w-24 flex-shrink-0 overflow-hidden rounded-xl transition-all sm:h-20 sm:w-28 ${
                i === index ? 'ring-2 ring-[#1fa5a3] ring-offset-2' : 'opacity-70 hover:opacity-100'
              }`}
            >
              <img src={img} alt={`${title} — ${i + 1}`} loading="lazy" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 p-4"
          onClick={() => { setLightbox(false); setZoomed(false); }}
        >
          <button
            type="button"
            aria-label="Cerrar"
            onClick={(e) => { e.stopPropagation(); setLightbox(false); setZoomed(false); }}
            className="absolute right-4 top-4 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20"
          >
            <X className="h-5 w-5" />
          </button>
          <span className="absolute left-1/2 top-4 -translate-x-1/2 rounded-full bg-white/10 px-3 py-1 text-sm font-medium text-white">
            {index + 1} / {count}
          </span>

          <div
            className={`relative max-h-[86vh] max-w-[92vw] overflow-hidden ${zoomed ? 'cursor-zoom-out' : 'cursor-zoom-in'}`}
            onClick={(e) => { e.stopPropagation(); setZoomed((z) => !z); }}
            onMouseMove={onPan}
            onMouseLeave={() => setOrigin({ x: 50, y: 50 })}
          >
            <img
              src={pics[index]}
              alt={`${title} — ${index + 1}`}
              draggable={false}
              className="max-h-[86vh] max-w-[92vw] select-none rounded-lg object-contain transition-transform duration-300"
              style={{
                transform: zoomed ? 'scale(2.3)' : 'scale(1)',
                transformOrigin: `${origin.x}% ${origin.y}%`,
              }}
            />
          </div>

          {count > 1 && (
            <>
              <Arrow dir={-1} onClick={() => go(index - 1)} />
              <Arrow dir={1} onClick={() => go(index + 1)} />
            </>
          )}
        </div>
      )}
    </div>
  );
}
